import { memo, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { campaignReportsAPI } from 'services/campaignReports.service';
import { campaignReportsActions } from 'store/actions';
import { Buttons, ImageWithFallback, CopyString, ParagraphWithTooltip, Loader } from 'components';
import { ArrowBack, Pen, LampIcon } from 'assets/icons';
import { subdirectory, getDynamicUrl, isDateWasReached, choseCampaignStatusIcon } from 'utils';
import { routes } from 'config';
import { ChangeBid } from './components/changeBid/ChangeBid';
import { ChangeBudget } from './components/changeBudget/ChangeBudget';
import { DotsMenu } from './components/dotsMenu/DotsMenu';
import { isAdminSelector } from 'store/selectors';
import * as S from './AppPanelCampaignReports.styled';

const bidType = {
  1: 'CPM',
  2: 'CPC',
  3: 'CPI',
};

function getTitle(status, isDraft) {
  if (status == 0) return <S.Status style={{ color: '#17ABA3' }}>Active</S.Status>;
  if (status == 3 && !isDraft) return <S.Status style={{ color: '#FCAF17' }}>Paused</S.Status>;
  if (status == 3 && isDraft) return <S.Status style={{ color: '#334155' }}>Draft</S.Status>;
  if (status == 4 || status == 8)
    return <S.Status style={{ color: '#EF4444' }}>Suspended</S.Status>;
  if (status == 9) return <S.Status style={{ color: '#94A3B8' }}>Archived</S.Status>;
}

export const AppPanelCampaignReports = memo(() => {
  const navigate = useNavigate();

  const { cmp_id } = useParams();
  const isAdmin = useSelector(isAdminSelector);
  const { data, isFetching, isLoading, isError } = campaignReportsAPI.useGetCampaignDataQuery({
    cm_id: cmp_id,
  });

  const copyId = data?.cm_campaignid?.toString() || '';

  const dispatch = useDispatch();

  useEffect(() => {
    if (data?.cm_name) {
      dispatch(
        campaignReportsActions.updateProperty({
          propField: 'campaignName',
          newValue: data?.cm_name,
        }),
      );
    }
  }, [isFetching]);

  const budgetLeft = ((data?.budget || 0) - (data?.daily_cost || 0)).toFixed(2);
  const isCpm = data?.bid_type === 1;
  const minBid = isCpm
    ? window.cpm_bid_minimum
    : isAdmin
    ? window.bid_minimum_admin
    : data?.min_bid;
  // to show actual (publisher-configured) minimum for admin users
  const displayMinBid = isCpm ? minBid : data?.min_bid ?? minBid;
  const maxBid = isCpm ? window.cpm_bid_maximum : window.bid_maximum;

  function goBack() {
    localStorage.setItem('scrollId', cmp_id);
    if (window.history.length > 2 && window.history.state?.idx) {
      navigate(-1);
    } else {
      const url = routes.list.find((route) => route.id === 'campaign').url;
      if (url === '/Discovery/Manage') console.log('url', url);
      navigate(url);
    }
  }

  const goToEditCampaign = () => {
    let url = getDynamicUrl(routes.list.find((route) => route.id === 'campaign/editCampaign').url, {
      cmp_id,
    });
    navigate(url);
  };

  const IconStatus = choseCampaignStatusIcon({
    campaignStatus: data?.campaign_status,
    payment_status: data?.payment_status,
    _hasBanners: data?.has_banners,
    banner_list_no_tracking: data?.banner_list_no_tracking,
    bid_type: data?.bid_type,
    schedulerStartDate: data?.scheduler_start_date,
    schedulerEndDate: data?.scheduler_end_date,
    startWasReached: isDateWasReached(data?.scheduler_start_date),
    endWasReached: isDateWasReached(data?.scheduler_end_date),
  });

  const showLoader = useMemo(() => {
    return isLoading || !data;
  }, [isLoading, data]);

  const isDraft = useMemo(() => {
    if (data?.campaign_status === 3) {
      return data?.has_banners === 0 || data?.banner_list_no_tracking !== '';
    }
    return (
      (data?.campaign_status === 3 &&
        data?.bid_type === 3 &&
        data?.has_banners === 0 &&
        data?.banner_list_no_tracking !== '') ||
      (data?.campaign_status === 3 && data?.bid_type === 2 && data?.has_banners === 0)
    );
  }, [data?.bid_type, data?._hasBanners, data?.banner_list_no_tracking, data?.campaign_status]);

  if (isError) {
    return null;
    // return (
    //   <h1 style={{ padding: '10px 0', border: '1px solid red', color: 'red', textAlign: 'center' }}>
    //     AppPanelCampaignReports Error
    //   </h1>
    // );
  }

  return (
    <S.AppPanelBox isLoading={showLoader}>
      {showLoader ? (
        <Loader.Local delay={0} />
      ) : (
        <>
          <S.BackArrow onClick={goBack}>
            <ArrowBack color='inherit' />
          </S.BackArrow>
          <S.Separator />
          <S.AppInfoBox style={{ gap: '8px', minWidth: '200px' }}>
            <S.MenuIconWrap>
              <DotsMenu {...data} />
            </S.MenuIconWrap>
            <div style={{ width: 'calc(100% - 52px)' }}>
              <S.NameAndIcon>
                <ParagraphWithTooltip text={data?.cm_name ?? ''} />
                <IconStatus style={{ flexShrink: '0' }} />
                {getTitle(data?.campaign_status, isDraft)}
              </S.NameAndIcon>
              <S.CopyWrap>
                <CopyString label='ID' text={copyId} />
              </S.CopyWrap>
            </div>
          </S.AppInfoBox>
          <div style={{ margin: '0 8px' }}>
            <p>Bid:</p>
            <S.TextContainer>
              {bidType[data.bid_type]}: <S.BoldText>${data.bid}</S.BoldText>
              <S.TextIcon>
                <LampIcon />
              </S.TextIcon>
              <S.TextIcon>
                <ChangeBid
                  bidValue={data?.bid}
                  minBid={minBid}
                  displayMinBid={displayMinBid}
                  maxBid={maxBid}
                  isCpm={isCpm}
                />
              </S.TextIcon>
            </S.TextContainer>
          </div>
          <div>
            <p>Daily Budget:</p>
            <S.TextContainer>
              <S.BoldText>${Number(data?.budget - budgetLeft)?.toFixed(2) || 0} </S.BoldText>
              <span> / </span>
              <S.DarkerText> ${Number(data?.budget)?.toFixed(2) || 0}</S.DarkerText>
              <S.TextIcon>
                <ChangeBudget budgetValue={data?.budget} />
              </S.TextIcon>
              <S.MyProgressBar
                max={data.budget}
                value={data.budget - budgetLeft}
                percents={data.budget ? 100 - (budgetLeft / data.budget) * 100 : 100}
              />
            </S.TextContainer>
          </div>
          <S.AppInfoBox style={{ margin: '0 8px' }}>
            <p style={{ width: '56px', textAlign: 'right' }}>Promoted app:</p>
            <S.ImageWrap>
              <ImageWithFallback
                width='40'
                height='40'
                src={`https://cdn.appnext.com/${subdirectory(data.image_url)}/${data.image_url}`}
              />
            </S.ImageWrap>
            <div>
              <S.BoldTextTitle>{data.pba_name}</S.BoldTextTitle>
              <p>{data.pba_url1}</p>
            </div>
          </S.AppInfoBox>
          <S.ButtonWrap>
            <Buttons.PrimaryNormal id='edit_btn' icon={<Pen />} onClick={goToEditCampaign}>
              Edit Campaign
            </Buttons.PrimaryNormal>
          </S.ButtonWrap>
        </>
      )}
    </S.AppPanelBox>
  );
});

AppPanelCampaignReports.displayName = 'AppPanelCampaignReports';
